/**
 * @class
 */
import Base from '@netx/core/lib/netx-model';

class Socket extends Base {
	/**
	 * @property {object}  defaults                      - Model defaults
	 * @property {boolean} defaults.autoConnect          - Auto connect to websockets at initialize
	 * @property {boolean} defaults.autoReconnect        - Auto reconnect when disconnected by server
	 * @property {boolean} defaults.clientDisconnected   - We opted to disconnect from websockets
	 * @property {boolean} defaults.connected            - Connected to websockets
	 * @property {array}   defaults.connectionDurations  - Connection duration store
	 * @property {number}  defaults.connectionRetries    - Current websocket connection retries (will reset after sustaining a successful connection for at least 5s)
	 * @property {number}  defaults.lastConnectTime      - The last time we achieved a websocket connection (ms)
	 * @property {number}  defaults.lastDisconnectTime   - The last time we were disconnected from websockets (ms)
	 * @property {number}  defaults.maxConnectionRetries - Max times we will try to reconnect without acheiving a successful connection fot at least 5s
	 * @property {number}  defaults.reconnectCount       - The number of times the websocket reconnected through the life of our session
	 * @property {array}   defaults.subscriptions        - Websocket subscriptions
	 * @property {string}  defaults.url                  - Websocket endpoint
	 */
	get defaults() {
		return {
			autoConnect: true,
			autoReconnect: true,
			clientDisconnected: false,
			connected: false,
			connectionDurations: [],
			connectionRetries: 0,
			disconnectEvents: [],
			lastConnectTime: 0,
			lastDisconnectTime: 0,
			maxConnectionRetries: 5,
			reconnectCount: 0,
			subscriptions: [],
			url: '',
		};
	}

	/**
	 * @override
	 * @param {object} attrs Model attributes
	 * @param {object} opts Request options
	 */
	constructor(attrs, opts) {
		super(attrs, opts);

		// Debounced/throttled methods
		this.reconnect = _.throttle(this.reconnect, 5000, true);

		// Hey, listen!
		this.listenTo(
			this,
			'change:connectionRetries',
			(model, connectionRetries) => {
				if (
					!this.get('connected') &&
					connectionRetries >= this.get('maxConnectionRetries')
				) {
					console.warn(
						'There is an issue connecting to websockets. Max retries reached.',
					);
				}
			},
		);

		// Auto connect
		// !Netx.session.get('token') && this.get('autoConnect') && this.connect();
	}

	/**
	 * @method
	 */
	connect() {
		this.whenConnected && this.whenConnected.reject();

		const whenConnected = (this.whenConnected = new $.Deferred());

		// DEBUG
		if (this.fail) {
			_.delay(() => {
				this.disconnected();
			}, 2);
			return this.whenConnected.resolve();
		}

		const socket = (this.socket = new WebSocket(this.get('url')));
		// Can check the event code here as well if need be
		socket.onclose = _.bind(this.disconnected, this);
		socket.onmessage = _.bind(this.message, this);

		// When connected
		socket.onopen = () => {
			this.set({
				clientDisconnected: false,
				connected: true,
				lastConnectTime: _.now(),
			});

			// Let's try and maintain a connection for at least 5s
			// before resetting max retries
			this.resetMaxConnectRetries = setTimeout(() => {
				this.set('connectionRetries', 0);
			}, 5000);

			whenConnected.resolve();
		};

		return this.whenConnected;
	}

	/**
	 * @method
	 */
	disconnect() {
		if (!this.get('connected')) return this;

		this.set('clientDisconnected', true);
		// https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent#Status_codes
		this.socket.close(1000);

		return this;
	}

	/**
	 * @method
	 * @return {number} Current connection lifetime in ms
	 */
	getConnectionLifetime() {
		return this.get('connected') ? _.now() - this.get('lastConnectTime') : 0;
	}

	/**
	 * @method
	 * @param {event} e `close` event
	 */
	disconnected(e) {
		// Reset
		this.resetMaxConnectRetries && clearTimeout(this.resetMaxConnectRetries);

		const connectionDurations = this.get('connectionDurations');
		const disconnectEvents = this.get('disconnectEvents');

		if (this.get('connected')) {
			this.set({
				connected: false,
				connectionDurations: connectionDurations.concat([
					_.now() - this.get('lastConnectTime'),
				]),
				disconnectEvents: disconnectEvents.concat([e]),
				lastDisconnectTime: _.now(),
			});
		}

		// We did not ask for this disconnect, assume something went wrong and reconnect
		// Typically caused by a `Broken pipe` on the server - this should be resolved when
		// websockets are rebuilt in 9.0
		if (!this.get('clientDisconnected') && this.get('autoReconnect')) {
			console.warn('client did not opt to disconnect');
			this.reconnect();
		} else {
			this.socket.onmessage = null;
			this.socket.onclose = null;
			this.off().set({
				connectionRetries: 0,
				subscriptions: [],
			});
		}
	}

	/**
	 * @method
	 * @param {event} e `message` event
	 */
	message(e) {
		const data = JSON.parse(e.data);

		data.roomName
			? this.trigger(
					'socket:message:' + data.roomName,
					JSON.parse(data.message),
			  )
			: this.trigger('socket:message', data);
	}

	/**
	 * @method
	 */
	reconnect() {
		const connectionRetries = this.get('connectionRetries');
		const maxConnectionRetries = this.get('maxConnectionRetries');

		if (connectionRetries < maxConnectionRetries) {
			console.warn(
				'attempting to reconnect to websocket (' +
					(connectionRetries + 1) +
					'/' +
					maxConnectionRetries +
					')',
			);

			var reconnectCount = this.get('reconnectCount');
			this.set({
				connectionRetries: connectionRetries + 1,
				reconnectCount: reconnectCount + 1,
			});

			this.connect().then(() => {
				// Re-subscribe when connected
				this.overrideSubscriptionCheck = true;
				_.each(this.get('subscriptions'), this.subscribe, this);
				this.overrideSubscriptionCheck = false;
			});
		}
	}

	/**
	 * @method
	 * @param {object} message Message
	 */
	send(message) {
		this.when(this.get('connected') ? true : this.whenConnected).then(() => {
			this.socket.send(JSON.stringify(message));
		});
		return this;
	}

	/**
	 * @method
	 * @param {string} roomName Room name
	 */
	subscribe(roomName) {
		const subscriptions = this.get('subscriptions');
		const inSubscriptions = _.contains(subscriptions, roomName);

		if (
			!roomName ||
			!this.get('connected') ||
			(!this.overrideSubscriptionCheck && inSubscriptions)
		)
			return this;

		!inSubscriptions &&
			this.set('subscriptions', subscriptions.concat([roomName]));

		return this.send({
			message: '',
			action: 6,
			roomName: roomName,
		});
	}

	/**
	 * @method
	 * @param {string} roomName Room name
	 */
	unsubscribe(roomName) {
		const subscriptions = this.get('subscriptions');

		if (
			!this.get('connected') ||
			(roomName && !_.contains(subscriptions, roomName))
		)
			return this;

		this.set(
			'subscriptions',
			roomName ? _.without(subscriptions, roomName) : [],
		);

		return this.send({
			message: '',
			action: 7,
			roomName: roomName,
		});
	}

	// DEBUG
	/** @method */
	simulateFail() {
		this.fail = true;
		this.disconnected();
	}

	/** @type {string} */
	static get __name__() {
		return 'Socket';
	}
}

export default Socket;
